/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type {
  ContentItem,
  ImportBatch,
  NewProduct,
  NewPromotionLink,
  NewSale,
  Platform,
  Product,
  PromotionLink,
  SalesImportInput,
  Storefront,
  WeSellData,
} from "../domain/types";
import {
  createProduct,
  createSale,
  createContentItem,
  createPromotionLink,
  importSales,
  loadData,
  resetDemo,
  saveConnectionSecret,
  triggerSync,
  updatePlatform,
  updateProduct,
  updateStorefront,
} from "../data/repository";
import { validateAffiliateLink } from "../lib/affiliateLinks";

interface ContextValue {
  data: WeSellData | null;
  loading: boolean;
  saving: boolean;
  error: string;
  isDemo: boolean;
  reload: () => Promise<void>;
  addProduct: (input: NewProduct) => Promise<Product>;
  editProduct: (id: string, patch: Partial<Product>) => Promise<Product>;
  addSale: (input: NewSale) => Promise<void>;
  addContent: (input: Omit<ContentItem, "id">) => Promise<void>;
  addPromotion: (input: NewPromotionLink) => Promise<PromotionLink>;
  addImportedSales: (input: SalesImportInput) => Promise<ImportBatch>;
  editPlatform: (id: string, patch: Partial<Platform>) => Promise<void>;
  editStorefront: (patch: Partial<Storefront>) => Promise<void>;
  connect: (
    platformSlug: string,
    mode: string,
    fields: Record<string, string>,
  ) => Promise<void>;
  sync: (platformSlug: string) => Promise<void>;
  restoreDemo: () => void;
}

const WeSellContext = createContext<ContextValue | null>(null);

export function WeSellProvider({
  children,
  isDemo,
}: {
  children: ReactNode;
  isDemo: boolean;
}) {
  const [data, setData] = useState<WeSellData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await loadData(isDemo));
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Não foi possível carregar seus dados.",
      );
    } finally {
      setLoading(false);
    }
  }, [isDemo]);

  useEffect(() => {
    // Initial data fetch intentionally drives provider state.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void reload();
  }, [reload]);

  const withData = useCallback(
    async <T,>(operation: (current: WeSellData) => Promise<T>): Promise<T> => {
      if (!data) throw new Error("Dados ainda não carregados.");
      setSaving(true);
      setError("");
      try {
        return await operation(data);
      } catch (cause) {
        const message =
          cause instanceof Error ? cause.message : "Não foi possível salvar.";
        setError(message);
        throw cause;
      } finally {
        setSaving(false);
      }
    },
    [data],
  );

  const addProduct = useCallback(
    (input: NewProduct) =>
      withData(async (current) => {
        const product = await createProduct(current, input, isDemo);
        setData({ ...current, products: [product, ...current.products] });
        return product;
      }),
    [isDemo, withData],
  );

  const editProduct = useCallback(
    (id: string, patch: Partial<Product>) =>
      withData(async (current) => {
        const product = await updateProduct(current, id, patch, isDemo);
        setData({
          ...current,
          products: current.products.map((item) =>
            item.id === id ? product : item,
          ),
        });
        return product;
      }),
    [isDemo, withData],
  );

  const addSale = useCallback(
    (input: NewSale) =>
      withData(async (current) => {
        const sale = await createSale(current, input, isDemo);
        setData({ ...current, sales: [sale, ...current.sales] });
      }),
    [isDemo, withData],
  );

  const addContent = useCallback(
    (input: Omit<ContentItem, "id">) =>
      withData(async (current) => {
        const content = await createContentItem(current, input, isDemo);
        setData({ ...current, contents: [content, ...current.contents] });
      }),
    [isDemo, withData],
  );

  const addPromotion = useCallback(
    (input: NewPromotionLink) =>
      withData(async (current) => {
        const platform = current.platforms.find(
          (item) => item.id === input.platformId,
        );
        if (!platform) throw new Error("Plataforma inválida.");
        const validated = validateAffiliateLink(
          platform,
          input.url,
          input.trackingId,
        ).valid;
        const promotion = await createPromotionLink(
          current,
          input,
          validated,
          isDemo,
        );
        setData({
          ...current,
          promotions: [promotion, ...current.promotions],
        });
        return promotion;
      }),
    [isDemo, withData],
  );

  const addImportedSales = useCallback(
    (input: SalesImportInput) =>
      withData(async (current) => {
        const imported = await importSales(current, input, isDemo);
        setData({
          ...current,
          sales: [...imported.sales, ...current.sales],
          imports: [imported.batch, ...current.imports],
        });
        return imported.batch;
      }),
    [isDemo, withData],
  );

  const editPlatform = useCallback(
    (id: string, patch: Partial<Platform>) =>
      withData(async (current) => {
        const platform = await updatePlatform(current, id, patch, isDemo);
        setData({
          ...current,
          platforms: current.platforms.map((item) =>
            item.id === id ? platform : item,
          ),
        });
      }),
    [isDemo, withData],
  );

  const editStorefront = useCallback(
    (patch: Partial<Storefront>) =>
      withData(async (current) => {
        const storefront = await updateStorefront(current, patch, isDemo);
        setData({ ...current, storefront });
      }),
    [isDemo, withData],
  );

  const connect = useCallback(
    async (
      platformSlug: string,
      mode: string,
      fields: Record<string, string>,
    ) => {
      setSaving(true);
      setError("");
      try {
        await saveConnectionSecret(platformSlug, mode, fields, isDemo);
        await reload();
      } finally {
        setSaving(false);
      }
    },
    [isDemo, reload],
  );

  const sync = useCallback(
    async (platformSlug: string) => {
      setSaving(true);
      setError("");
      try {
        await triggerSync(platformSlug, isDemo);
        await reload();
      } finally {
        setSaving(false);
      }
    },
    [isDemo, reload],
  );

  const restoreDemo = useCallback(() => setData(resetDemo()), []);

  const value = useMemo<ContextValue>(
    () => ({
      data,
      loading,
      saving,
      error,
      isDemo,
      reload,
      addProduct,
      editProduct,
      addSale,
      addContent,
      addPromotion,
      addImportedSales,
      editPlatform,
      editStorefront,
      connect,
      sync,
      restoreDemo,
    }),
    [
      data,
      loading,
      saving,
      error,
      isDemo,
      reload,
      addProduct,
      editProduct,
      addSale,
      addContent,
      addPromotion,
      addImportedSales,
      editPlatform,
      editStorefront,
      connect,
      sync,
      restoreDemo,
    ],
  );

  return (
    <WeSellContext.Provider value={value}>{children}</WeSellContext.Provider>
  );
}

export function useWeSell(): ContextValue {
  const context = useContext(WeSellContext);
  if (!context)
    throw new Error("useWeSell precisa estar dentro de WeSellProvider.");
  return context;
}
